import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Auth } from "aws-amplify";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IActionMeta } from "src/app-redux/store";
import { ax } from "src/common/api/apiClient";
import { IApi } from "src/common/typings/app";
import { IUser } from "src/common/typings/user";
import { userAttribs } from "src/common/utils/userAttribs4mLocalStorage";
import { AuthState } from "../authSlice";

type ILoginArgs = {
  username: string;
  password: string;
  newPassword: string;
};
type IVerifyOTPArgs = {
  userName: string;
  code: string;
  rememberMe: boolean;
};
export const loginFailed = (
  state: AuthState,
  action: PayloadAction<
    unknown,
    string,
    IActionMeta<ILoginArgs>,
    { code?: string; message?: string; name?: string }
  >
) => {
  if (action.error.code === "PasswordResetRequiredException") {
    state.status = "passwordResetRequired";
  } else state.status = "failed";
  delete state.user;
  state.authError = action?.error?.message || "An Unexpected Error Occurred";
};
export const handleLogin = (state: AuthState) => {
  state.status = "idle";
  delete state.authError;
};
export const loginAction = createAsyncThunk(
  "auth/loginAction",
  async (user: ILoginArgs, {}): Promise<IUser> => {
    try {
      const res = await ax.post<IApi>("/login", user);
      if (!res.data.value.twoFARequired) {
        localStorage.clear();
        localStorage.setItem("token", res.data.value.token);
        localStorage.setItem("refreshToken", res.data.value.refreshToken);
        localStorage.setItem("nodUserMenus", JSON.stringify(res.data.value.userMenus));
        localStorage.setItem("nodUserFullName", res.data.value.userFullName);
        localStorage.setItem("userName", res.data.value.userName);
        localStorage.setItem("nodUserId", res.data.value.userId);
        localStorage.setItem("terminalType", res.data.value.terminalType);
        localStorage.setItem("terminalName", res.data.value.terminalName);
        localStorage.setItem("terminalId", res.data.value.terminalId);
        localStorage.setItem(
          "isAllowToExportData",
          res.data.value?.isAllowToExportData?.toString()
        );
        return res.data.value;
      } else if (res.data.value.twoFARequired) {
        localStorage.clear();
        localStorage.setItem("showOtpForm", JSON.stringify(true));
        localStorage.setItem(
          "otpTime",
          JSON.stringify(`${new Date().getMinutes()}:${new Date().getSeconds()}`)
        );
        return res.data.value;
      } else throw new Error("Unable to extract cognito user info from localStorage");
    } catch (error: any) {
      if (error.code === "PasswordResetRequiredException")
        throw { code: error.code, message: "Password Reset Required" };
      throw error;
    }
  }
);

export const verifyOTPAction = createAsyncThunk(
  "auth/verifyOTPAction",
  async (request: IVerifyOTPArgs): Promise<IUser> => {
    try {
      const res = await ax.post<IApi>("/verify-2fa", request);
      if (res.data.isSuccess) {
        localStorage.clear();
        localStorage.setItem("token", res.data.value.token);
        localStorage.setItem("refreshToken", res.data.value.refreshToken);
        localStorage.setItem("nodUserMenus", JSON.stringify(res.data.value.userMenus));
        localStorage.setItem("nodUserFullName", res.data.value.userFullName);
        localStorage.setItem("nodUserId", res.data.value.userId);
        return res.data.value;
      } else throw new Error("Unable to extract cognito user info from localStorage");
    } catch (error: any) {
      throw error;
    }
  }
);

/* RenewToken */

export const renewAccessToken = createAsyncThunk("auth/renewAccessToken", async () => {
  try {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const newForm = { token: `${token}`, refreshToken: `${refreshToken}` };
    const res = await ax.post("/renew-token", newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.data.isSuccess === true) {
      localStorage.clear();
      localStorage.setItem("token", res.data.value.token);
      localStorage.setItem("refreshToken", res.data.value.refreshToken);
      return res.data.value;
    } else throw new Error("Unable to extract cognito user info from localStorage");
  } catch (error: any) {
    throw error;
  }
});
/* ./RenewToken */

export const currentUserAction = createAsyncThunk("auth/currentUserAction", async () => {
  try {
    await Auth.currentAuthenticatedUser();
    const attribs = userAttribs();
    if (attribs) return JSON.parse(attribs) as IUser;
    else throw new Error("Unable to extract cognito user info from localStorage");
  } catch (error) {
    throw error;
  }
});

export const handleLogout = (state: AuthState) => {
  state.status = "idle";
  delete state.authError;
};

export const logOutAction = createAsyncThunk("auth/logOutAction", async () => {
  const token = localStorage.getItem("token");
  try {
    const res = await ax.post<IApi>("/logout", { token: token });
    if (res.data.isSuccess === true && res.data.value.isLogOutUserSuccessFull === true) {
      return res.data.value.isLogOutUserSuccessFull;
    }
  } catch (error) {
    throw error;
  }
});

type ISetPassArgs = {
  Email: string;
  Password: string;
};
export const setPasswordAction = createAsyncThunk(
  "management/setPassword",
  async (user: ISetPassArgs, { dispatch }) => {
    try {
      const res = await ax.post<IApi>("/user-set-password", user, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "Password has been changed successfully!",
            options: { variant: "success" },
          })
        );
        return user;
      } else throw new Error(res.data.error || res.statusText);
    } catch (error) {
      throw error;
    }
  }
);
